import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';

const TripDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [trip, setTrip] = useState(null);

  useEffect(() => {
    fetchTrip();
  }, [id]);

  const fetchTrip = async () => {
    const { data } = await get(`/api/trips/${id}`);
    if (data) setTrip(data);
  };

  if (loading) return <LoadingSpinner />;
  if (!trip) return <div className="p-6">Trip not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/trips/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Trip Details</h1>
        </div>
        <button
          onClick={() => navigate(`/trips/edit/${id}`)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
        >
          <PencilIcon className="h-5 w-5" />
          <span>Edit</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500">ID</label>
            <p className="text-lg font-medium">{trip.id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Label</label>
            <p className="text-lg font-medium">{trip.label || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Type</label>
            <p className="text-lg font-medium">{trip.type || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Driver</label>
            <p className="text-lg font-medium">{trip.driver?.fullName || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Vehicle</label>
            <p className="text-lg font-medium">
              {trip.vehicle ? `${trip.vehicle.brand} - ${trip.vehicle.plates}` : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Started At</label>
            <p className="text-lg font-medium">
              {trip.startedAt ? new Date(trip.startedAt).toLocaleString() : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Ended At</label>
            <p className="text-lg font-medium">
              {trip.endedAt ? new Date(trip.endedAt).toLocaleString() : '-'}
            </p>
          </div>
        </div>

        {/* Devices Section */}
        {trip.devices && trip.devices.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Devices</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">ID</th>
                    <th className="text-left py-2">Brand</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trip.devices.map(device => (
                    <tr key={device.id} className="border-b last:border-0">
                      <td className="py-2">{device.id}</td>
                      <td className="py-2">{device.brand}</td>
                      <td className="py-2">{device.devType}</td>
                      <td className="py-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          device.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {device.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Events Section */}
        {trip.events && trip.events.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Events</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">ID</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Timestamp</th>
                    <th className="text-left py-2">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {trip.events.map(event => (
                    <tr key={event.id} className="border-b last:border-0">
                      <td className="py-2">{event.id}</td>
                      <td className="py-2">{event.eventType?.description || '-'}</td>
                      <td className="py-2">{new Date(event.timestamp).toLocaleString()}</td>
                      <td className="py-2">{event.referenceNum || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* GPS Records Section */}
        {trip.gpsRecords && trip.gpsRecords.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">GPS Records</h2>
            <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Timestamp</th>
                    <th className="text-left py-2">Latitude</th>
                    <th className="text-left py-2">Longitude</th>
                    <th className="text-left py-2">Speed</th>
                  </tr>
                </thead>
                <tbody>
                  {trip.gpsRecords.map((record, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="py-2">{new Date(record.timestamp).toLocaleString()}</td>
                      <td className="py-2">{record.latitude}</td>
                      <td className="py-2">{record.longitude}</td>
                      <td className="py-2">{record.speed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripDetails;